import { useEffect, useState } from "react";
import { Clock } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { findHoliday, holidayName } from "@/lib/holidays-sg";

const COPY = {
  de: {
    open_now: "Sekretariat jetzt erreichbar",
    closed_now: "Sekretariat geschlossen",
    holiday_today: (name) => `Heute Feiertag · ${name}`,
    open_until: (h) => `bis ${h} Uhr`,
    open_again: (day, h) => `Wieder ab ${day} · ${h} Uhr`,
    days: ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
    today: "heute",
    tomorrow: "morgen",
  },
  fr: {
    open_now: "Bureau joignable maintenant",
    closed_now: "Bureau fermé",
    holiday_today: (name) => `Jour férié · ${name}`,
    open_until: (h) => `jusqu'à ${h}h`,
    open_again: (day, h) => `Ré-ouverture ${day} · ${h}h`,
    days: ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"],
    today: "aujourd'hui",
    tomorrow: "demain",
  },
  en: {
    open_now: "Office reachable now",
    closed_now: "Office closed",
    holiday_today: (name) => `Public holiday · ${name}`,
    open_until: (h) => `until ${h}:00`,
    open_again: (day, h) => `Re-opens ${day} · ${h}:00`,
    days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    today: "today",
    tomorrow: "tomorrow",
  },
  it: {
    open_now: "Segreteria raggiungibile ora",
    closed_now: "Segreteria chiusa",
    holiday_today: (name) => `Festività · ${name}`,
    open_until: (h) => `fino alle ${h}:00`,
    open_again: (day, h) => `Riapre ${day} · ${h}:00`,
    days: ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"],
    today: "oggi",
    tomorrow: "domani",
  },
};

// Regular business hours · Mo–Do 07–17, Fr 07–16. Sa/So closed.
const HOURS = {
  1: [7, 17], 2: [7, 17], 3: [7, 17], 4: [7, 17],
  5: [7, 16],
  0: null, 6: null,
};

function shiftDate(base, days) {
  const x = new Date(base.getFullYear(), base.getMonth(), base.getDate());
  x.setDate(x.getDate() + days);
  return x;
}

function computeStatus(now) {
  const todayHoliday = findHoliday(now);
  const day = now.getDay();
  const hour = now.getHours() + now.getMinutes() / 60;
  const slot = HOURS[day];

  // Currently open?  Only if not a holiday AND within hours.
  if (!todayHoliday && slot && hour >= slot[0] && hour < slot[1]) {
    return {
      open: true,
      closeHour: slot[1],
      reopenOffset: null,
      reopenHour: null,
      holiday: null,
    };
  }

  // Find next opening — must skip weekends AND holidays. We look up to
  // 14 days ahead to handle clusters like Karfreitag + Easter weekend +
  // Ostermontag combined with normal weekends.
  for (let offset = 0; offset < 14; offset++) {
    const candidate = shiftDate(now, offset);
    const wd = candidate.getDay();
    const candidateSlot = HOURS[wd];
    if (!candidateSlot) continue; // weekend
    if (findHoliday(candidate)) continue; // holiday

    // Today's slot still ahead (before opening hour)?
    if (offset === 0 && hour < candidateSlot[0] && !todayHoliday) {
      return {
        open: false,
        closeHour: null,
        reopenOffset: 0,
        reopenHour: candidateSlot[0],
        holiday: null,
      };
    }
    // Today already closed (after hours or holiday) → look further.
    if (offset === 0) continue;

    return {
      open: false,
      closeHour: null,
      reopenOffset: offset,
      reopenHour: candidateSlot[0],
      holiday: todayHoliday,
    };
  }

  return {
    open: false,
    closeHour: null,
    reopenOffset: null,
    reopenHour: null,
    holiday: todayHoliday,
  };
}

export default function OfficeStatus({ className = "" }) {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // Re-evaluate every minute so the status flips at opening/closing time
    // and around midnight without a page reload.
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const s = computeStatus(now);

  // Headline (line 1)
  let line1;
  if (s.open) {
    line1 = c.open_now;
  } else if (s.holiday) {
    line1 = c.holiday_today(holidayName(s.holiday.key, lang));
  } else {
    line1 = c.closed_now;
  }

  // Sub-line (line 2)
  let line2;
  if (s.open) {
    line2 = c.open_until(s.closeHour);
  } else if (s.reopenOffset != null) {
    const targetDay = (now.getDay() + s.reopenOffset) % 7;
    const dayLabel =
      s.reopenOffset === 0
        ? c.today
        : s.reopenOffset === 1
        ? c.tomorrow
        : c.days[targetDay];
    line2 = c.open_again(dayLabel, s.reopenHour);
  } else {
    line2 = "—";
  }

  return (
    <div
      data-testid="office-status"
      data-state={s.open ? "open" : s.holiday ? "holiday" : "closed"}
      className={`inline-flex items-center gap-3 px-3 py-2 border border-white/10 bg-white/[0.03] backdrop-blur-sm ${className}`}
    >
      <span
        className={`relative flex-none w-2 h-2 rounded-full ${
          s.open ? "bg-emerald-400" : "bg-amber-400"
        }`}
      >
        {s.open && (
          <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-60" />
        )}
      </span>
      <Clock className="w-3.5 h-3.5 text-zinc-400 flex-none" strokeWidth={1.5} />
      <div className="leading-tight">
        <div className="text-[11px] uppercase tracking-[0.18em] text-white">
          {line1}
        </div>
        <div className="text-[10px] text-zinc-400 mt-0.5">{line2}</div>
      </div>
    </div>
  );
}
